// Copyright (c) StrangeBee 2024

import client from '@Client';
import { actionListRenderer, ActionListRendererParams } from '@Helpers/actionListRenderer';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { ResponderDrawer } from '@Organisms/Responder';
import RouteBuilder from '@Services/RouteBuilder';
import { ListActionType } from '@Types';
import React, { ReactElement } from 'react';
import {
    ObservableOutput,
    ObjectTypeV1 as ObjectType,
    AnalyzerOutput,
    Config,
    AdminPermissions,
    OrgPermissions,
    OrgPermissionsEnum,
} from 'thehive-sdk';
import { ObservableBulkEditManage } from '@Organisms/Observable/ObservableBulkEdit/ObservableBulkEditManage';
import AnalyzerDrawer from '@Organisms/Analyzer/AnalyzerDrawer/AnalyzerDrawer';
import { PinContextInterface, PinType, PinTypes } from '@Molecules/Pin';
import { ObservableExport } from './ObservableExport/ObservableExport';
import { ICONS } from '@Constants/iconsApp';
import { THPopConfirm } from '@Molecules/Inputs/THPopConfirm/THPopConfirm';
import { ObservableEventList } from './eventList';
import { escapeURI } from '@Helpers/string';
import { copyToClipboard } from '@Helpers/copy';
import { UseLocalMessageType } from '@Helpers/locale';

type ExecuteJobProps = {
    data?: AnalyzerOutput[];
    onFinish?: (...args: any[]) => any;
};

const computeDataPin =
    (caseId: string) =>
    ({ _id, data, attachment }: Partial<ObservableOutput>): PinType | null => {
        const url = RouteBuilder.compute('cases.observable', { caseId, observableId: _id });
        return _id && (data || attachment)
            ? { caseId, type: PinTypes.OBSERVABLE, id: _id, title: data || attachment?.name || _id, url }
            : null;
    };

const convertDataToPin = (getDataPin: (...args: any[]) => any, selectedItems: ObservableOutput[]): PinType[] =>
    selectedItems.reduce((acc: PinType[], curr): PinType[] => {
        const result = getDataPin(curr);
        return result !== null ? [...acc, result] : acc;
    }, [] as PinType[]);

export const actionList = (
    locale: UseLocalMessageType,
    history: any,
    parentType: ObjectType,
    caseId: string,
    pinContext?: Partial<PinContextInterface>,
    analyzer?: ExecuteJobProps,
    hasCortex?: boolean,
    eventList?: ObservableEventList,
    statusConfig?: Config,
    permissions: (AdminPermissions | OrgPermissions)[] = [],
): ActionListRendererParams<ObservableOutput> =>
    actionListRenderer<ObservableOutput>(
        (
            selectedItems,
            onSuccess,
            from,
        ): {
            [key: string]: ListActionType;
        } => {
            const observableIdList = selectedItems.map(({ _id }) => _id);
            const isMultiple = observableIdList.length > 1 ? 'multiple' : 'single';

            const { removeMultiPin, addMultiPin, isPinned, getLimit } = pinContext || {};

            const getDataPin = computeDataPin(caseId);
            const limitPin = getLimit?.({ caseId, type: PinTypes.OBSERVABLE });

            const isAlert = parentType === ObjectType.ALERT;

            const data = (selectedItems[0] || {}) as ObservableOutput;

            return {
                edit: {
                    label: locale('observable.actions.edit.label'),
                    icon: <ICONS.EditIcon />,
                    scope: 'list',
                    CustomComponent: (triggerElem): ReactElement => (
                        <ObservableBulkEditManage
                            onBulkEdit={(observables, fieldEdited) =>
                                client.ObservableController.multiEdit(
                                    observables.map((o) => o._id),
                                    fieldEdited,
                                )
                                    .then(() => onSuccess(false))
                                    .catch(handleNotificationError)
                            }
                            triggerElem={triggerElem}
                            observables={selectedItems}
                        />
                    ),
                    hidden: isAlert
                        ? !permissions?.includes(OrgPermissionsEnum.MANAGE_OBSERVABLE) ||
                          !permissions?.includes(OrgPermissionsEnum.MANAGE_ALERT_UPDATE)
                        : !permissions?.includes(OrgPermissionsEnum.MANAGE_OBSERVABLE),
                },
                delete: {
                    query: (): void => {
                        client.ObservableController.multipleDelete(observableIdList).then(() => {
                            openMessage('success', locale('notification.observable.delete'));
                            if (from === 'details') {
                                if (!isAlert) {
                                    history.push(RouteBuilder.compute('cases.observables', { caseId }));
                                }
                            } else {
                                onSuccess(true);
                            }
                        });
                    },
                    message: locale(`observable.actions.delete.${isMultiple}`),
                    label: locale('observable.actions.delete.label'),
                    icon: <ICONS.DeleteIcon />,
                    confirm: true,
                    hidden: isAlert
                        ? !permissions?.includes(OrgPermissionsEnum.MANAGE_OBSERVABLE) ||
                          !permissions?.includes(OrgPermissionsEnum.MANAGE_ALERT_UPDATE)
                        : !permissions?.includes(OrgPermissionsEnum.MANAGE_OBSERVABLE),
                },
                analyzers: {
                    label: locale('observable.actions.analyzers.label'),
                    icon: <ICONS.FireOutlined />,
                    CustomComponent: (triggerElem): ReactElement => (
                        <AnalyzerDrawer
                            items={selectedItems}
                            triggerElem={triggerElem}
                            analyzer={analyzer?.data || []}
                            onFinish={(items) => analyzer?.onFinish?.(items).then(() => onSuccess(true))}
                        />
                    ),
                    hidden: !permissions?.includes(OrgPermissionsEnum.MANAGE_ANALYSE) || !analyzer || !hasCortex,
                    scope: 'both',
                },
                responders: {
                    label: locale('observable.actions.responders.label'),
                    icon: <ICONS.RunResponderIcon />,
                    hidden:
                        !permissions?.includes(OrgPermissionsEnum.MANAGE_ACTION) ||
                        observableIdList.length > 1 ||
                        !hasCortex,
                    CustomComponent: (triggerElem): ReactElement => (
                        <ResponderDrawer
                            objectId={observableIdList.find(Boolean) || ''}
                            objectType="case_artifact"
                            triggerElem={triggerElem}
                        />
                    ),
                },
                unpin: {
                    query: (): void => {
                        const dataPins = convertDataToPin(getDataPin, selectedItems);
                        removeMultiPin?.(dataPins);
                    },
                    label: locale('common.unpin'),
                    hidden: !removeMultiPin || isAlert || selectedItems.some((data) => !isPinned?.(getDataPin(data))),
                    scope: 'both',
                    icon: <ICONS.UnpinIcon />,
                },
                pin: {
                    query: (): void => {
                        const dataPins = convertDataToPin(getDataPin, selectedItems);
                        addMultiPin?.(dataPins);
                    },
                    label: locale('common.pin'),
                    disabled: !limitPin,
                    hidden: !addMultiPin || isAlert || selectedItems.some((data) => isPinned?.(getDataPin(data))),
                    scope: 'both',
                    icon: <ICONS.PinIcon />,
                },
                export: {
                    label: locale('observable.actions.export.label'),
                    icon: <ICONS.ExportOutlined />,
                    CustomComponent: (triggerElem): ReactElement => (
                        <ObservableExport selectedObservables={selectedItems} triggerElem={triggerElem} />
                    ),
                },
                copy: {
                    scope: 'single',
                    label: locale('observable.actions.copy.label'),
                    icon: <ICONS.CopyOutlined />,
                    query: () => {
                        copyToClipboard(data.data || data.attachment?.name || '', locale('notification.copy'));
                    },
                },
                download: {
                    scope: 'single',
                    label: locale('general.download'),
                    icon: <ICONS.DownloadOutlined />,
                    hidden: !eventList || !data?.attachment || !data?.attachment?._id,
                    CustomComponent: (triggerElem): ReactElement => {
                        return (
                            <THPopConfirm
                                title={locale('observable.actions.zip-password', {
                                    password: statusConfig?.protectDownloadsWith || '',
                                })}
                                onConfirm={() => {
                                    eventList &&
                                        window.open(
                                            eventList.getDownload({
                                                observableId: data._id,
                                                objectId: data?.attachment?._id || '',
                                                objectName: escapeURI(data?.attachment?.name || ''),
                                            }),
                                            '_blank',
                                        );
                                }}
                                placement="top"
                            >
                                <span>{triggerElem}</span>
                            </THPopConfirm>
                        );
                    },
                },
            };
        },
    );
